import { def, isArr } from 'lib/util'
import { useMemo } from 'react'
import Chip, { fontSizeMapping } from 'ui/Element/Text/Chip'
import { useMemoRef } from 'platform/react/hook'
import settings from '@tenant/settings'

/**
 * StatusChip UI element
 *
 * Simple element to display a {@link Chip} with the correct color for a specific status.
 *
 * @param {Object} p
 * @param {React.Ref<Chip>} p.forwardedRef  ref to the element
 * @param {Gaia.Component.Events} p.events  an object containing an onOpen event handler
 * @param {String} p.value                  a string to be used as value
 * @param {boolean} [p.dot=false]           displays the chip with a dot as an icon, bold text and
 *                                          no background
 * @param {Object} [p.decorators]           additional decorators
 * @returns {JSX.Element|null}              the new Text element
 */
const StatusChip = ({ forwardedRef, events, value, dot = false, ...props }) => {
  const [currentStatus = {}] = useMemo(() => (
    value && isArr(value) ? value : events?.onOpen?.(value) || [{}]
  ), [value, events])

  const { lowercaseStatuses } = settings

  const decorator = useMemo(() => (
    { uppercase: lowercaseStatuses && def(lowercaseStatuses) ? !lowercaseStatuses : true, ...props.decorators || {} }
  ), [props.decorators])

  return !currentStatus?.value ? null : (
    <Chip
      {...props}
      decorators={decorator}
      forwardedRef={forwardedRef}
      value={currentStatus.value}
      color={currentStatus.color}
      backgroundColor={currentStatus.backgroundColor}
      {...dot && {
        icon: {
          name: currentStatus?.icon?.name || 'dot',
          variant: currentStatus?.variant?.variant || 'filled',
        },
        backgroundColor: 'transparent',
        // 12/medium => 12/bold
        textVariant: `${fontSizeMapping[props.size || 'medium'].split('/')[0]}/bold`,
      }}
    />
  )
}

export default useMemoRef(StatusChip, props => [
  props.value,
  props.variant,
  props.events,
  props.dot,
])
